import { Link } from "react-router-dom";
import "../../styles/theme-dark.css";
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  TextField,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Checklist as ChecklistIcon
} from '@mui/icons-material';

const ChecklistTemplates = () => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [expandedBlocks, setExpandedBlocks] = useState({});
  
  // Form states
  const [blockForm, setBlockForm] = useState({ nombre: '' });
  const [itemsText, setItemsText] = useState('');
  
  // Feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Cargar bloques con items
  const loadBlocks = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/bloques');
      if (response.ok) {
        const data = await response.json();
        setBlocks(data.blocks || []);
      } else {
        console.error('Error cargando bloques:', response.status);
        showSnackbar('Error cargando bloques', 'error');
      }
    } catch (error) {
      console.error('Error cargando bloques:', error);
      showSnackbar('Error cargando bloques', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlocks();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Crear o actualizar bloque
  const handleSaveBlock = async () => {
    if (!blockForm.nombre.trim()) {
      showSnackbar('El nombre del bloque es obligatorio', 'error');
      return;
    }

    const itemsArray = itemsText.split('\n')
      .map(item => item.trim())
      .filter(item => item.length > 0);

    if (itemsArray.length === 0) {
      showSnackbar('Debe agregar al menos un item', 'error');
      return;
    }

    try {
      if (editingBlock) {
        // Actualizar bloque
        await fetch(`/api/bloques/block/${editingBlock.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nombre: blockForm.nombre })
        });

        // Eliminar items existentes
        for (const item of editingBlock.items) {
          await fetch(`/api/bloques/item/${item.id}`, {
            method: 'DELETE'
          });
        }

        // Crear nuevos items
        for (const [index, texto] of itemsArray.entries()) {
          await fetch('/api/bloques/item', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              blockId: editingBlock.id, 
              texto,
              orden: index + 1
            })
          });
        }

        showSnackbar('Bloque actualizado correctamente');
      } else {
        // Crear nuevo bloque
        const blockResponse = await fetch('/api/bloques/block', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nombre: blockForm.nombre })
        });

        if (blockResponse.ok) {
          const { block } = await blockResponse.json();
          
          // Crear items
          for (const [index, texto] of itemsArray.entries()) {
            await fetch('/api/bloques/item', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                blockId: block.id, 
                texto,
                orden: index + 1
              })
            });
          }

          showSnackbar('Bloque creado correctamente');
        } else {
          const errorData = await blockResponse.json();
          showSnackbar(errorData.error || 'Error creando bloque', 'error');
        }
      }

      setOpenDialog(false);
      setBlockForm({ nombre: '' });
      setItemsText('');
      setEditingBlock(null);
      loadBlocks();
    } catch (error) {
      console.error('Error guardando bloque:', error);
      showSnackbar('Error guardando bloque', 'error');
    }
  };

  // Eliminar bloque
  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('¿Eliminar este bloque y todos sus items?')) return;

    try {
      const response = await fetch(`/api/bloques/block/${blockId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showSnackbar('Bloque eliminado');
        loadBlocks();
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Error eliminando bloque', 'error');
      }
    } catch (error) {
      console.error('Error eliminando bloque:', error);
      showSnackbar('Error eliminando bloque', 'error');
    }
  };

  // Abrir diálogo para editar
  const handleEditBlock = (block) => {
    setEditingBlock(block);
    setBlockForm({ nombre: block.nombre });
    setItemsText(block.items.map(item => item.texto).join('\n'));
    setOpenDialog(true);
  };

  // Abrir diálogo para nuevo
  const handleNewBlock = () => {
    setEditingBlock(null);
    setBlockForm({ nombre: '' });
    setItemsText('');
    setOpenDialog(true);
  };

  // Cancelar diálogo
  const handleCancelDialog = () => {
    setOpenDialog(false);
    setEditingBlock(null);
    setBlockForm({ nombre: '' });
    setItemsText('');
  };

  // Toggle expandir/contraer bloque
  const toggleBlock = (blockId) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [blockId]: !prev[blockId]
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="page-dark" style={{ minHeight: "100vh", padding: 16 }}>
      <Box sx={{ p: 3, background: "transparent" }}>
        {/* HEADER */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ color: "white" }}>
              <ChecklistIcon sx={{ mr: 1, verticalAlign: 'middle', color: "#60a5fa" }} />
              Bloques de Checklist para Instalaciones
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 1 }}>
              Crea bloques con items reutilizables para checklists de instalación.
            </Typography>
          </Box>
          
          <Box display="flex" gap={2} alignItems="center">
            {/* BOTÓN NUEVO BLOQUE */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewBlock}
              className="glass-btn"
              style={{ 
                background: "rgba(59, 130, 246, 0.2)",
                border: "1px solid rgba(59, 130, 246, 0.4)",
                color: "white"
              }}
            >
              Nuevo Bloque
            </Button>
            
            {/* BOTÓN VOLVER */}
            <Link 
              to="/tecnicos" 
              className="glass-btn" 
              style={{ 
                fontSize: 13,
                padding: "8px 16px",
                textDecoration: "none",
                color: "white"
              }}
            >
              ← Volver a Técnicos
            </Link>
          </Box>
        </Box>

        {blocks.length === 0 ? (
          <Box
            sx={{
              background: "rgba(15,23,42,0.65)",
              borderRadius: "14px",
              border: "1px solid rgba(148,163,184,0.25)",
              backdropFilter: "blur(12px)",
              p: 4,
              textAlign: "center",
              boxShadow: "0 10px 25px rgba(0,0,0,0.3)"
            }}
          >
            <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.9)", mb: 2 }}>
              No hay bloques creados
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mb: 3 }}>
              Crea tu primer bloque con items para empezar
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleNewBlock}
              sx={{
                color: "#60a5fa",
                borderColor: "rgba(96, 165, 250, 0.4)",
                '&:hover': {
                  borderColor: "#60a5fa",
                  background: "rgba(96, 165, 250, 0.1)"
                }
              }}
            >
              Crear primer bloque
            </Button>
          </Box>
        ) : (
          blocks.map(block => (
            <Card 
              key={block.id} 
              sx={{ 
                mb: 3,
                background: "rgba(15,23,42,0.65)",
                border: "1px solid rgba(148,163,184,0.25)",
                backdropFilter: "blur(12px)",
                boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
                color: "white"
              }}
            >
              <CardContent sx={{ color: "white" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <IconButton 
                      onClick={() => toggleBlock(block.id)} 
                      size="small"
                      sx={{ color: "white", mr: 1 }}
                    >
                      {expandedBlocks[block.id] ? (
                        <Typography sx={{ fontSize: 12 }}>▼</Typography>
                      ) : (
                        <Typography sx={{ fontSize: 12 }}>▶</Typography>
                      )}
                    </IconButton>
                    <Typography variant="h6" sx={{ color: "white" }}>
                      {block.nombre}
                    </Typography>
                    <Chip 
                      label={`${block.items.length} items`}
                      size="small" 
                      sx={{ ml: 2, color: "white", background: "rgba(255,255,255,0.1)" }}
                    />
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEditBlock(block)}
                      sx={{ color: "#60a5fa", mr: 1 }}
                      title="Editar bloque"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteBlock(block.id)}
                      sx={{ color: "#f87171" }}
                      title="Eliminar bloque"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                {expandedBlocks[block.id] && (
                  <Box sx={{ ml: 4, mt: 2 }}>
                    {block.items.length === 0 ? (
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", fontStyle: "italic" }}>
                        No hay items en este bloque
                      </Typography>
                    ) : (
                      <Paper 
                        sx={{ 
                          p: 2,
                          background: "rgba(30,41,59,0.65)",
                          border: "1px solid rgba(148,163,184,0.2)",
                          color: "white"
                        }}
                      >
                        <List dense>
                          {block.items.map((item, index) => (
                            <React.Fragment key={item.id}>
                              <ListItem sx={{ pl: 0, py: 0.5, color: "white" }}>
                                <Typography variant="body2" sx={{ color: "white", mr: 1 }}>
                                  {index + 1}.
                                </Typography>
                                <ListItemText 
                                  primary={item.texto}
                                  primaryTypographyProps={{ 
                                    variant: 'body2',
                                    sx: { color: "white" }
                                  }}
                                />
                              </ListItem>
                              {index < block.items.length - 1 && (
                                <Divider component="li" sx={{ borderColor: "rgba(255,255,255,0.1)", my: 0.5 }} />
                              )}
                            </React.Fragment>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))
        )}

        {/* Dialog para crear/editar bloque */}
        <Dialog 
          open={openDialog} 
          onClose={handleCancelDialog} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              background: "rgba(15,23,42,0.9)",
              border: "1px solid rgba(148,163,184,0.3)",
              color: "white"
            }
          }}
        >
          <DialogTitle sx={{ color: "white", pb: 1 }}>
            {editingBlock ? 'Editar Bloque' : 'Nuevo Bloque'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nombre del bloque"
              fullWidth
              value={blockForm.nombre}
              onChange={(e) => setBlockForm({ ...blockForm, nombre: e.target.value })}
              sx={{ mb: 3 }}
              InputLabelProps={{ sx: { color: "rgba(255,255,255,0.7)" } }}
              InputProps={{ sx: { color: "white" } }}
              placeholder="Ej: INSTALACIÓN, VERIFICACIONES, DOCUMENTACIÓN..."
            />
            
            <TextField
              margin="dense"
              label="Items (uno por línea)"
              fullWidth
              multiline
              rows={8}
              value={itemsText}
              onChange={(e) => setItemsText(e.target.value)}
              InputLabelProps={{ sx: { color: "rgba(255,255,255,0.7)" } }}
              InputProps={{ sx: { color: "white" } }}
              placeholder={`Ej:
Verificar conexión eléctrica
Comprobar nivelación de la máquina
Revisar presión de neumáticos
Confirmar funcionamiento de sensores
...`}
              helperText="Escribe cada item en una línea separada"
              FormHelperTextProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleCancelDialog}
              sx={{ 
                color: "rgba(255,255,255,0.8)",
                '&:hover': { color: "white" }
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveBlock} 
              variant="contained"
              sx={{
                background: "rgba(59, 130, 246, 0.8)",
                '&:hover': { background: "rgba(59, 130, 246, 1)" }
              }}
            >
              {editingBlock ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default ChecklistTemplates;